<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('user.title') }}
        </h6>

        <CreateButton
            :label="$t('user.create')"
            :to="{ name: 'userForm' }"
            v-if="!isCurrentUserCoordinator"/>

      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('user.list') }}
        </h5>

        <Search :filters="filters"
                :loading="isSearching"
                :officeDropdown="officeDropdown"
                :lastNameDropdown="lastNameDropdown"
                :engagementTypeDropdown="engagementTypeDropdown"
                :statusDropdown="statusDropdown"
                @search="getUsers"/>

        <div class="row align-items-center justify-content-between">
          <div>
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getUsers"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>

            <ExcelExportButton
                :label="$t('general.excel_export_monthly_details')"
                :loading="isExcelQualificationsLoaded"
                @click="getExcelExportQualifications"/>
          </div>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!users.data?.length"
            icon="fa-users"
            title="Keine Mitarbeiter gefunden"
            subtitle="Es wurden keine Mitarbeiter gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th></th>
              <th>
                <a href="#" @click="sortBy('employeeNumber')">
                  <span>Mitarbeiternummer</span>
                  <i :class="getIconClass('employeeNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('status')">
                  <span>Status</span>
                  <i :class="getIconClass('status')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('engagementType')" style="min-width: 200px;">
                  <span>Extern/Intern</span>
                  <i :class="getIconClass('engagementType')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('startingDate')" style="min-width: 200px;">
                  Eintrittstermin
                  <i :class="getIconClass('startingDate')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('firstName')">
                  Vorname
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('lastName')">
                  Nachname
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('userAccessRights')">
                  Rechte
                  <i :class="getIconClass('userAccessRights')"></i>
                </a>
              </th>
              <th>
                Standort
              </th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(user) in users.data" :key="user.id">
              <td>
                <div v-if="piniaCurrentUser.roles[0] === 'ROLE_ADMIN' ||
                           piniaCurrentUser.roles[0] === 'ROLE_PERSONAL' ||
                           piniaCurrentUser.roles[0] === 'ROLE_HEAD_OF_HR'"
                     @click="impersonate(user.id, user?.roles)"
                     class="os-icon os-icon-eye"
                     style="cursor: pointer; font-size: 16px; color: #389142;">
                </div>
              </td>

              <td>{{ user?.employeeNumber }}</td>
              <td>{{ user?.statusTranslation }}</td>
              <td>{{ user?.engagementTypeTranslation }}</td>
              <td>{{ user?.startingDate }}</td>
              <td>{{ user?.firstName }}</td>
              <td>{{ user?.lastName }}</td>
              <td>{{ user?.userAccessRightsTranslation }}</td>

              <td>
                <div
                    :class="{ 'popover-trigger': user.locations && user.locations.length > 5 }"
                    @mouseenter="handleMouseEnter($event, user)"
                    @mouseleave="user.locations && user.locations.length > 5 && hidePopover">
                  <template v-if="user.locations && user.locations.length > 5">
                    <template
                        v-for="(location, index) in user.locations.slice(0, 3)"
                        :key="location.id">
                      {{ location.name }}
                      <span v-if="index < 2">, </span>
                    </template>
                    <span
                        class="badge badge-primary ml-1 px-2 py-1 align-middle"
                        style="font-size: 0.75rem; font-weight: 600;">
                      +{{ user.locations.length - 3 }}
                   </span>
                  </template>
                  <template v-else-if="user.locations && user.locations.length > 0">
                    <template
                        v-for="(location, index) in user.locations"
                        :key="location.id">
                      {{ location.name }}
                      <span v-if="index < user.locations.length - 1">, </span>
                    </template>
                  </template>
                  <template v-else>
                    <span>Keine Daten</span>
                  </template>
                </div>
              </td>

              <Teleport to="body">
                <div
                    v-if="showLocationsPopover"
                    class="locations-popover"
                    :style="popoverStyle"
                    @mouseenter="keepPopover"
                    @mouseleave="hidePopover">
                  <div
                      v-for="location in activeLocations"
                      :key="location.id">
                    {{ location.name }}
                  </div>
                </div>
              </Teleport>

              <td class="text-right">
                <div>
                  <!-- Edit button -->
                  <button v-if="!isCurrentUserCoordinator" class="btn btn-primary"
                          @click="router.push({ name: 'userForm', params: { id: user?.id } })">
                    <i class="os-icon os-icon-edit"></i>
                  </button>
                  <button v-else class="btn btn-primary"
                          @click="router.push({ name: 'userBasicInfo', params: { id: user?.id } })">
                    <i class="os-icon os-icon-edit"></i>
                  </button>

                  <!-- Delete button -->
                  <button class="btn btn-danger ml-2" @click="deleteModal(user?.id)" v-if="!isCurrentUserCoordinator">
                    <i class="os-icon os-icon-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="users?.pagination?.total_pages > 1"
            :totalPages="users?.pagination?.total_pages"
            :currentPage="users?.pagination?.current_page"
            :totalItems="users?.pagination?.total_items"
            :perPage="users?.pagination?.items_per_page"
            @pagechanged="getUsers"/>
      </div>
    </div>

    <Teleport to="body">
      <div
          v-if="visible && activeUser"
          class="popover-content"
          :style="{
            top: position.top + 'px',
            left: position.left + 'px'}"
          @mouseenter="visible = true"
          @mouseleave="hidePopover">
    <span
        v-for="location in activeUser.locations"
        :key="location.id">
      {{ location.name }}<br/>
    </span>
      </div>
    </Teleport>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteUser">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup>
import securityService from '@/api/Security';
import userService from '@/api/User';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getOfficeDropdown, getUserLastNameDropdown} from '@/shared/dropdowns';
import {downloadFile, formatRole, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import Search from '@/views/App/User/Search.vue';
import debounce from 'lodash/debounce';
import {computed, nextTick, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);
const currentUserRole = piniaCurrentUser.value.roles[0];
const isCurrentUserCoordinator = computed(() => piniaUserState.currentUser.isCoordinator);
const getEnums = computed(() => piniaEnumsState.enums);
const users = ref([]);
const token = ref(null);
const isExcelLoaded = ref(false);
const isExcelQualificationsLoaded = ref(false);
const filters = ref({
  officeId: '',
  lastName: '',
  engagementType: '',
  type: '',
  status: '',
  orderBy: 'DESC',
  sortBy: '',
});

const filterDebounce = ref({
  searchTerm: '',
});

const showModal = ref(false);
const userId = ref(0);
const isSearching = ref(false);
const isLoaded = ref(false);
const lastNameDropdown = ref([]);
const officeDropdown = ref([]);
const engagementTypeDropdown = ref([]);
const statusDropdown = ref([]);

const debouncedSearch = debounce(async (query) => {
  await getUsers(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getUsers(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = users.value?.pagination?.items_per_page;

  try {
    const {data} = await userService.getUsers({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    users.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteUser() {
  try {
    await userService.deleteUser(userId.value);

    filters.value.officeId = '';
    filters.value.lastName = '';
    filters.value.engagementType = '';
    filters.value.type = '';
    filters.value.status = '';
    filters.value.orderBy = 'DESC'; // Keep this as 'DESC' if you don't want to change it
    filters.value.sortBy = '';
    showModal.value = false;

    await getUsers();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getIconClass(key) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getUsers();
}

function deleteModal(id) {
  CloseToast();
  userId.value = id;
  showModal.value = true;
}

async function getUserEnumDropdowns() {
  statusDropdown.value = getEnums.value?.userStatus || [];

  if (currentUserRole === "ROLE_ACCOUNTANT") {
    engagementTypeDropdown.value = getEnums.value?.userEngagementTypes.filter(item => item.value === 'extern');
  } else {
    engagementTypeDropdown.value = getEnums.value?.userEngagementTypes || [];
  }

}

async function impersonate(userId, userRoles) {
  try {
    const bodyData = {id: userId};
    const {data} = await securityService.impersonate(bodyData);

    token.value = data.data.token;

    localStorage.setItem('token', data.data.token);
    const userStore = piniaUserStore();
    await userStore.piniaGetUser();

    await router.push({name: `dashboard-${formatRole(userRoles[0])}`});
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getExcelExport() {
  await downloadFile('/user/excel/export', undefined,
      {...filters.value, searchTerm: filterDebounce.value.searchTerm},
      false,
      "GET",
      isExcelLoaded
  )
}

async function getExcelExportQualifications() {
  await downloadFile('/user/excel/export-with-qualifications', undefined,
      {...filters.value, searchTerm: filterDebounce.value.searchTerm},
      false,
      'GET',
      isExcelQualificationsLoaded
  )
}

const showLocationsPopover = ref(false)
const activeLocations = ref([])
const popoverStyle = ref({})

const hideTimeout = ref(null)

const showPopover = async (event, user) => {
  clearTimeout(hideTimeout.value)

  const currentTarget = event.currentTarget
  if (!currentTarget) return
  const rect = currentTarget.getBoundingClientRect()

  activeLocations.value = user.locations

  popoverStyle.value = {
    position: 'fixed',
    visibility: 'hidden',
    top: '0px',
    left: '0px',
    zIndex: 9999
  }

  showLocationsPopover.value = true

  await nextTick()

  requestAnimationFrame(() => {
    const popup = document.querySelector('.locations-popover')

    if (!popup) {
      return
    }

    const popupWidth = popup.getBoundingClientRect().width

    let left = rect.left - popupWidth - 10

    if (left < 10) {
      left = rect.right + 10
    }

    popoverStyle.value = {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${left}px`,
      zIndex: 9999,
      visibility: 'visible'
    }
  })
}


const hidePopover = () => {
  hideTimeout.value = setTimeout(() => {
    showLocationsPopover.value = false
    activeLocations.value = []
  }, 100)
}

const keepPopover = () => {
  clearTimeout(hideTimeout.value)
}

const handleMouseEnter = (event, user) => {
  // If the user has 5 or fewer locations,
  // close any currently opened popover.
  if (!user.locations || user.locations.length <= 5) {
    hidePopover()
    return
  }
  showPopover(event, user)
}

onMounted(async () => {
  await Promise.all([
    getUserEnumDropdowns(),
    getOfficeDropdown(officeDropdown, t),
    getUserLastNameDropdown(lastNameDropdown),
    getUsers(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>
@media (max-width: 629px) {
  .customExcelExport {
    min-width: 100% !important;
    margin-top: 10px;
    margin-left: 10px !important;
  }
}

.popover-trigger {
  cursor: pointer;
}

.locations-popover {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px 15px;
  min-width: 180px;
  max-width: 300px;
  max-height: 350px;
  overflow-y: auto;
  font-size: 14px;
}

.locations-popover div {
  white-space: normal;
  word-break: break-word;
}


</style>